import { useMemo } from 'react'
import { Bug } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { SecCrudTable, type SecField } from './SecCrudTable'
import { SEC_STR as S, type PentestRow } from './securityMoreApi'

/**
 * A18.22 — Pentest. Admin-managed pentest findings registry (`sec_pentest`,
 * 000248): title + severity + affected component + remediation status, `found_at`
 * = when logged. Real CRUD via `/admin/security/pentest` — findings are recorded
 * by hand from any vendor report, functional with no live bridge.
 */
const SEVERITY_OPTS = [
  { value: 'critical', label: 'critical' },
  { value: 'high', label: 'high' },
  { value: 'medium', label: 'medium' },
  { value: 'low', label: 'low' },
  { value: 'info', label: 'info' },
]
const STATUS_OPTS = [
  { value: 'open', label: 'open' },
  { value: 'fixed', label: 'fixed' },
  { value: 'accepted', label: 'accepted' },
]

export function PentestScreen() {
  const columns = useMemo<AdminColumn<PentestRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text' },
      {
        key: 'severity',
        header: S.severity,
        type: 'enum',
        width: 120,
        options: [
          { value: 'critical', label: S.critical, status: 'banned' },
          { value: 'high', label: S.high, status: 'blocked' },
          { value: 'medium', label: S.medium, status: 'review' },
          { value: 'low', label: S.low, status: 'pending' },
          { value: 'info', label: S.info, status: 'active' },
        ],
      },
      { key: 'component', header: S.component, type: 'text', width: 170 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'open', label: S.openLbl, status: 'open' },
          { value: 'fixed', label: 'fixed', status: 'active' },
          { value: 'accepted', label: 'accepted', status: 'pending' },
        ],
      },
      { key: 'notes', header: S.notes, type: 'text', minWidth: 160 },
      { key: 'found_at', header: S.foundAt, type: 'date', width: 150 },
    ],
    [],
  )

  const fields = useMemo<SecField[]>(
    () => [
      { name: 'title', placeholder: S.title },
      { name: 'severity', placeholder: S.severity, type: 'select', options: SEVERITY_OPTS },
      { name: 'component', placeholder: S.component },
      { name: 'status', placeholder: S.status, type: 'select', options: STATUS_OPTS },
      { name: 'notes', placeholder: S.notes },
    ],
    [],
  )

  return (
    <SecCrudTable<PentestRow>
      screenId="A18.22"
      title={S.pentest}
      addLabel={S.addPentest}
      path="/admin/security/pentest"
      listKey="findings"
      searchKeys={['title', 'component', 'severity']}
      fields={fields}
      columns={columns}
      icon={<Bug size={28} strokeWidth={1.5} />}
    />
  )
}
